import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router';
import clsx from 'clsx';
import { BuildingComplex, ChevronsUpDown, Pin, PinOff, X } from 'lucide-react';

import MovoFleetLogo from '@/shared/ui/branding/MovoFleetLogo';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import {
  SHELL_TOPBAR_BORDER,
  SHELL_TOPBAR_HEIGHT,
  SHELL_TOPBAR_SURFACE,
} from '@/app/layout/shellLayoutTokens';
import { isNavigationItemActive } from '@/app/layout/navigation';

const ICON_RAIL_CLASS = 'w-16 shrink-0';

/** Testo che appare a destra del rail icone senza spostare logo/avatar/icona */
const expandRevealClass = (expanded) =>
  clsx(
    'min-w-0 overflow-hidden transition-[opacity,max-width] duration-200 ease-out',
    expanded ? 'max-w-54 flex-1 opacity-100' : 'pointer-events-none max-w-0 flex-none opacity-0'
  );

const navRowClass = (isActive) =>
  clsx(
    'flex h-9 w-full items-center rounded-lg text-sm transition-colors',
    isActive ? 'bg-info-subtle text-info' : 'text-muted hover:bg-muted hover:text-strong'
  );

function IconRail({ children, tall = false }) {
  return (
    <span
      className={clsx(ICON_RAIL_CLASS, 'flex items-center justify-center', tall ? 'h-12' : 'h-9')}
    >
      {children}
    </span>
  );
}

function SidebarSeparator({ className = 'my-2' }) {
  return (
    <div
      role="separator"
      aria-orientation="horizontal"
      className={clsx('mx-2 shrink-0 border-t border-default', className)}
    />
  );
}

function splitPrimaryItems(items) {
  const main = items.filter((item) => item.id !== 'settings');
  const settings = items.filter((item) => item.id === 'settings');
  return { main, settings };
}

function renderNavItem(
  item,
  { location, isExpanded, onNavClick, navRowClass, expandRevealClass, IconRail }
) {
  const isActive = isNavigationItemActive(location.pathname, item);
  const Icon = item.icon;

  return (
    <Link
      key={item.id}
      to={item.path}
      onClick={onNavClick}
      title={!isExpanded ? item.label : undefined}
      className={navRowClass(isActive)}
    >
      <IconRail>
        <Icon
          className={clsx('h-[18px] w-[18px]', isActive ? 'text-brand' : 'text-subtle')}
          strokeWidth={1.75}
        />
      </IconRail>
      <span
        className={clsx(expandRevealClass(isExpanded), 'truncate pr-2 font-medium')}
        aria-hidden={!isExpanded}
      >
        {item.label}
      </span>
    </Link>
  );
}

export default function SessionSidebar({
  navigationSections,
  activeTenant,
  availableTenants,
  onSwitchTenant,
  isMobileOpen,
  onMobileClose,
  isPinned,
  onPinnedChange,
  tenantsLoading,
  tenantAutoSwitching,
}) {
  const location = useLocation();
  const [isHovered, setIsHovered] = useState(false);
  const hoverTimeoutRef = useRef(null);

  const isExpanded = isMobileOpen || isPinned || isHovered;

  useEffect(
    () => () => {
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    },
    []
  );

  useEffect(() => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    if (!isPinned) {
      setIsHovered(false);
    }
  }, [location.pathname, isPinned]);

  const handleNavClick = () => {
    onMobileClose?.();
    if (!isPinned) {
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
      setIsHovered(false);
    }
  };

  const handleMouseEnter = () => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    if (isPinned || isMobileOpen) return;
    hoverTimeoutRef.current = setTimeout(() => setIsHovered(false), 120);
  };

  const sidebarContent = (
    <div className="flex h-full flex-col">
      {/* Header — allineato alla top bar pagina (stessa altezza/superficie) */}
      <div
        className={clsx(
          SHELL_TOPBAR_HEIGHT,
          SHELL_TOPBAR_SURFACE,
          'shrink-0 border-b',
          SHELL_TOPBAR_BORDER
        )}
      >
        <DropdownMenu>
          <DropdownMenuTrigger
            className={clsx(
              SHELL_TOPBAR_HEIGHT,
              'flex w-full items-center rounded-none transition-colors',
              'hover:bg-subtle focus-visible:focus-ring-inset',
              'data-[state=open]:bg-subtle'
            )}
            title={activeTenant?.name || 'Seleziona tenant'}
          >
            <IconRail tall>
              <div className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-lg border border-default bg-default p-1 shadow-resting">
                <MovoFleetLogo variant="sidebarMark" className="object-center" />
              </div>
            </IconRail>
            <div className={clsx(expandRevealClass(isExpanded), 'flex items-center pr-2')}>
              <div className="min-w-0 flex-1 overflow-hidden">
                <p className="truncate text-sm font-medium leading-tight text-strong">
                  {activeTenant?.name || 'Movo Fleet'}
                </p>
                <p className="truncate text-xs leading-tight text-subtle">
                  {activeTenant?.slug || 'Sala operativa'}
                </p>
              </div>
              <ChevronsUpDown className="ml-1 h-4 w-4 shrink-0 text-placeholder" aria-hidden />
            </div>
          </DropdownMenuTrigger>

          <DropdownMenuContent align="start" alignOffset={8} className="w-56">
            <DropdownMenuLabel>Tenant operativo</DropdownMenuLabel>
            {availableTenants.length === 0 ? (
              <p className="px-2 py-2 text-xs text-subtle">Nessun tenant disponibile</p>
            ) : (
              <DropdownMenuRadioGroup value={activeTenant?._id ?? ''}>
                {availableTenants.map((tenant) => (
                  <DropdownMenuRadioItem
                    key={tenant._id}
                    value={tenant._id}
                    disabled={tenantsLoading || tenantAutoSwitching}
                    onSelect={() => onSwitchTenant?.(tenant._id)}
                  >
                    <BuildingComplex className="h-4 w-4 shrink-0 text-placeholder" aria-hidden />
                    <span className="min-w-0 flex-1 truncate">{tenant.name}</span>
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Navigation */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden py-1">
        <nav className="space-y-1">
          {navigationSections.map((section, sectionIndex) => {
            const isPrimary = section.id === 'primary';
            const { main, settings } = isPrimary
              ? splitPrimaryItems(section.items)
              : { main: section.items, settings: [] };

            return (
              <React.Fragment key={section.id}>
                {sectionIndex > 0 ? <SidebarSeparator className="my-1" /> : null}

                <div className="space-y-0.5">
                  {!isPrimary && section.label ? (
                    <p
                      className={clsx(
                        'overflow-hidden pl-16 pr-2 text-field-label uppercase text-subtle transition-[height,opacity] duration-200 ease-out',
                        isExpanded ? 'h-5 opacity-100' : 'h-0 opacity-0'
                      )}
                      aria-hidden={!isExpanded}
                    >
                      {section.label}
                    </p>
                  ) : null}

                  {main.map((item) =>
                    renderNavItem(item, {
                      location,
                      isExpanded,
                      onNavClick: handleNavClick,
                      navRowClass,
                      expandRevealClass,
                      IconRail,
                    })
                  )}

                  {settings.length > 0 ? (
                    <>
                      <SidebarSeparator className="my-0.5" />
                      {settings.map((item) =>
                        renderNavItem(item, {
                          location,
                          isExpanded,
                          onNavClick: handleNavClick,
                          navRowClass,
                          expandRevealClass,
                          IconRail,
                        })
                      )}
                    </>
                  ) : null}
                </div>
              </React.Fragment>
            );
          })}
        </nav>
      </div>

      <SidebarSeparator />

      <div className="py-2">
        <button
          type="button"
          onClick={() => onPinnedChange?.(!isPinned)}
          aria-pressed={isPinned}
          aria-label={isPinned ? 'Sblocca sidebar' : 'Fissa sidebar aperta'}
          title={!isExpanded ? 'Fissa sidebar aperta' : undefined}
          className={clsx(navRowClass(isPinned), 'focus-visible:focus-ring')}
        >
          <IconRail>
            {isPinned ? (
              <PinOff className="h-[18px] w-[18px] text-info" strokeWidth={1.75} aria-hidden />
            ) : (
              <Pin className="h-[18px] w-[18px] text-subtle" strokeWidth={1.75} aria-hidden />
            )}
          </IconRail>
          <span
            className={clsx(expandRevealClass(isExpanded), 'truncate pr-2 text-left font-medium')}
            aria-hidden
          >
            {isPinned ? 'Sblocca sidebar' : 'Fissa sidebar'}
          </span>
        </button>
      </div>
    </div>
  );

  return (
    <>
      {isMobileOpen ? (
        <button
          type="button"
          className="fixed inset-0 z-40 bg-backdrop/50 lg:hidden focus-visible:focus-ring"
          onClick={onMobileClose}
          aria-label="Chiudi menu"
        />
      ) : null}

      <aside
        aria-label="Navigazione"
        className={clsx(
          'fixed inset-y-0 left-0 z-50 hidden overflow-hidden border-r border-default bg-default text-strong transition-[width] duration-200 ease-out lg:block lg:rounded-br-2xl',
          isExpanded ? 'w-70' : 'w-16',
          isExpanded && !isPinned && 'shadow-overlay'
        )}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {sidebarContent}
      </aside>

      <aside
        aria-label="Navigazione mobile"
        className={clsx(
          'fixed inset-y-0 left-0 z-50 w-72 transform rounded-r-xl border-r border-default bg-default text-strong transition-transform duration-300 ease-out lg:hidden',
          isMobileOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex items-center justify-end border-b border-default px-3 py-2">
          <button
            type="button"
            onClick={onMobileClose}
            className="rounded-md p-2 text-subtle transition hover:bg-muted hover:text-strong focus-visible:focus-ring"
            aria-label="Chiudi menu"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        {sidebarContent}
      </aside>
    </>
  );
}
